import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErr, fmtDate, parseFecha } from "@/lib/format";
import { useCompany } from "@/contexts/CompanyContext";
import NewCaiModal from "@/pages/_settings/NewCaiModal";
import CaiSwitch from "@/pages/_settings/CaiSwitch";
import {
  ShieldCheckIcon, PlusIcon, ArrowsClockwiseIcon, WarningIcon, CalendarBlankIcon, MapPinIcon, HashIcon,
  ClockCountdownIcon, CalendarPlusIcon,
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

/**
 * Autorizaciones fiscales (CAI) por sucursal × documento + historial de configuraciones.
 * Integra lo que antes estaba en "Ventas y facturación → Actualización CAI": vista por sucursal,
 * alertas de vencimiento / correlativos por agotarse, registro y renovación, historial completo.
 * Todo se guarda en la misma fuente (cai_configurations) que usan los documentos al emitirse.
 */
export const TIPO_LABEL = {
  factura: "Factura", nota_credito: "Nota de Crédito", nota_debito: "Nota de Débito", guia_remision: "Guía de Remisión",
};
const TIPO_COLOR = {
  factura: "text-agro-green border-agro-green/40 bg-agro-green/10",
  nota_credito: "text-agro-yellow border-agro-yellow/40 bg-agro-yellow/10",
  nota_debito: "text-blue-400 border-blue-500/40 bg-blue-500/10",
  guia_remision: "text-purple-300 border-purple-400/40 bg-purple-500/10",
};
export const ESTADO_CLS = {
  vigente: "bg-agro-green/15 text-agro-green border-agro-green/40",
  programada: "bg-agro-yellow/15 text-agro-yellow border-agro-yellow/40",
  vencida: "bg-red-500/15 text-red-300 border-red-500/40",
  agotada: "bg-red-500/15 text-red-300 border-red-500/40",
  inhabilitada: "bg-text-muted/15 text-text-muted border-text-muted/40",
  historica: "bg-text-muted/15 text-text-muted border-text-muted/40",
};
// La guía de remisión y la proforma usan numeración interna (sin autorización del SAR)
const TIPOS = ["factura", "nota_credito", "nota_debito"];

export default function FiscalAuthorizations({ reloadKey = 0, onChanged, comun, branch = null }) {
  const { branches } = useCompany();
  const [summary, setSummary] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [fTipo, setFTipo] = useState("");
  const [fEstado, setFEstado] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: h }] = await Promise.all([
        api.get("/cai-configurations/summary/by-branch"),
        api.get("/cai-configurations", { params: branch ? { branch_id: branch.id } : {} }),
      ]);
      setSummary((s || []).filter((r) => !branch || r.branch_id === branch.id));
      setHistory(h || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }, [branch]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const openNew = (branchId = "", tipo = "factura") =>
    setModal({ branch_id: branch?.id || branchId, tipo_documento: tipo,
               direccion_fiscal: branch ? (branch.direccion || "") : (comun?.direccion || "") });

  const filtered = useMemo(() => history.filter((c) =>
    (!fTipo || c.tipo_documento === fTipo) && (!fEstado || c.estado === fEstado)), [history, fTipo, fEstado]);

  const [historial, setHistorial] = useState(null);
  const [editando, setEditando] = useState(null);

  return (
    <div className="space-y-5" data-testid="fiscal-authorizations">
      {/* ---------- Autorizaciones vigentes por sucursal ---------- */}
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-border/50 flex gap-3 items-start">
          <ShieldCheckIcon size={20} className="text-agro-green" weight="duotone" />
          <div className="flex-1">
            <div className="font-heading">Autorizaciones fiscales (CAI)</div>
            <div className="text-[11px] text-text-muted mt-0.5">
              {branch ? `Autorizaciones de la sucursal ${branch.nombre}: solo se usan para los documentos que emite esta sucursal. ` : "Una autorización por sucursal y documento. "}
              Regístrala con anticipación indicando desde qué fecha se usa:
              el sistema la activa ese día, evita vigencias y rangos superpuestos y bloquea la emisión con rango agotado
              o vencido.
            </div>
          </div>
          <button type="button" onClick={() => openNew()} data-testid="btn-nueva-cai"
            className="inline-flex items-center gap-2 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md px-3 py-2 text-xs">
            <PlusIcon size={14} weight="bold" /> Nueva autorización
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="h-7 w-7 border-2 border-agro-green border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {summary.map((row) => (
              <div key={row.branch_id || "legacy"} data-testid={`branch-${row.branch_id || "legacy"}`}>
                <div className="bg-sidebar px-5 py-2 text-xs">
                  <span className="text-[10px] uppercase tracking-wider text-text-muted mr-2">Sucursal</span>
                  <span className="font-semibold">{row.branch_codigo} · {row.branch_nombre}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-border/40">
                  {TIPOS.map((tipo) => (
                    <CaiCard key={tipo} tipo={tipo} cai={row.cais?.[tipo]} programadas={row.programadas?.[tipo] || []}
                             onNew={() => openNew(row.branch_id, tipo)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---------- Historial de configuraciones ---------- */}
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="cai-history">
        <div className="p-4 border-b border-border/60 flex items-center gap-3 flex-wrap">
          <h3 className="font-heading text-sm font-semibold flex items-center gap-2">
            <ClockCountdownIcon size={16} weight="duotone" className="text-text-secondary" />
            Historial y programación de vigencias ({filtered.length})
          </h3>
          <select value={fTipo} onChange={(e) => setFTipo(e.target.value)} data-testid="hist-f-tipo"
                  className="ml-auto bg-app border border-border rounded px-2 py-1 text-xs">
            <option value="">Todos los documentos</option>
            {TIPOS.map((t) => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
          </select>
          <select value={fEstado} onChange={(e) => setFEstado(e.target.value)} data-testid="hist-f-estado"
                  className="bg-app border border-border rounded px-2 py-1 text-xs">
            <option value="">Todos los estados</option>
            {Object.keys(ESTADO_CLS).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <button onClick={load} className="text-xs text-text-muted hover:text-agro-green flex items-center gap-1">
            <ArrowsClockwiseIcon size={13} /> Actualizar
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr>
                <th className="p-2.5 text-left pl-4">Sucursal</th><th className="p-2.5 text-left">Documento</th>
                <th className="p-2.5 text-left">CAI</th><th className="p-2.5 text-left">Rango</th>
                <th className="p-2.5 text-left">Desde</th><th className="p-2.5 text-left">Límite</th>
                <th className="p-2.5 text-right">Emitidos</th><th className="p-2.5 text-right">Restantes</th>
                <th className="p-2.5 text-center">Estado</th><th className="p-2.5 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="p-6 text-center text-xs text-text-muted">Sin autorizaciones</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-border/40 hover:bg-sidebar/40" data-testid={`cai-row-${c.id}`}>
                  <td className="p-2.5 pl-4 text-xs text-text-secondary">{c.branch_nombre}</td>
                  <td className="p-2.5 text-xs">
                    <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded border ${TIPO_COLOR[c.tipo_documento] || ""}`}>
                      {TIPO_LABEL[c.tipo_documento] || c.tipo_documento}
                    </span>
                  </td>
                  <td className="p-2.5 text-xs font-mono text-text-secondary truncate max-w-[200px]" title={`${c.numero_cai}\n${c.direccion_fiscal || ""}`}>{c.numero_cai}</td>
                  <td className="p-2.5 text-[11px] font-mono text-text-muted">{c.rango_inicial}<br />{c.rango_final}</td>
                  <td className="p-2.5 text-xs" title={c.entra_en_vigor}>{fmtDate(c.fecha_inicio || c.fecha_activacion)}
                    {c.se_inhabilita && <div className="text-[10px] text-agro-yellow">Se inhabilita el {c.se_inhabilita}</div>}
                    {c.programado && <div className="text-[10px] text-agro-yellow">En vigor: {c.entra_en_vigor}</div>}</td>
                  <td className="p-2.5 text-xs">{fmtDate(c.fecha_limite)}</td>
                  <td className="p-2.5 text-xs text-agro-yellow text-right font-mono">{c.documentos_emitidos}</td>
                  <td className="p-2.5 text-xs text-agro-green text-right font-mono">{c.documentos_restantes}
                    <div className="text-[10px] text-text-muted" data-testid={`cai-proximo-${c.id}`}>próx. {String(c.correlativo_actual).padStart(8, "0")}</div>
                    {c.inicio_programado?.numero && <div className="text-[10px] text-blue-300">desde {fmtDate(c.inicio_programado.fecha)}: {String(c.inicio_programado.numero).padStart(8, "0")}</div>}</td>
                  <td className="p-2.5 text-center">
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${ESTADO_CLS[c.estado] || ESTADO_CLS.historica}`}
                          title={c.motivo_desactivacion || ""}>{c.estado}</span>
                  </td>
                  <td className="p-2.5 pr-4 text-right">
                    <button onClick={() => setHistorial(c)} className="text-[11px] text-text-muted hover:text-agro-green mr-2"
                            data-testid={`cai-hist-${c.id}`}>Historial</button>
                    <button onClick={() => setEditando(c)} className="text-[11px] text-text-muted hover:text-agro-green mr-2"
                            data-testid={`cai-edit-${c.id}`}>EDITAR</button>
                    <CaiSwitch cai={c} onChanged={() => { load(); onChanged?.(); }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {historial && <CaiHistorial cai={historial} onClose={() => setHistorial(null)} />}
      {editando && (
        <NewCaiModal editing={editando} defaults={{}} branches={branches || []} onClose={() => setEditando(null)}
                     onSaved={() => { setEditando(null); load(); onChanged?.(); }} />
      )}
      {modal && (
        <NewCaiModal defaults={modal} branches={branches || []} lockBranch={branch} onClose={() => setModal(null)}
                     onSaved={() => { setModal(null); load(); onChanged?.(); }} />
      )}
    </div>
  );
}

const ACCION = {
  registrada: "Registrada", activada_por_vigencia: "Entró en vigor (automático)", editada: "Editada",
  inhabilitada_por_sustitucion: "Inhabilitada por sustitución", inhabilitacion_programada: "Inhabilitación programada",
  inhabilitada_manual: "Inhabilitada manualmente", agotada: "Rango agotado", reemplazada: "Reemplazada sin usarse",
  activada_manual: "Activada manualmente",
};

/* Historial de una autorización: quién, cuándo, valores anteriores / nuevos y fecha efectiva */
export function CaiHistorial({ cai, onClose }) {
  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto" role="dialog"
           onClick={(e) => e.stopPropagation()} data-testid="cai-historial">
        <div className="p-4 border-b border-border">
          <h3 className="font-heading text-lg">Historial · CAI {cai.numero_cai}</h3>
          <div className="text-[11px] text-text-muted">{cai.rango_inicial} a {cai.rango_final} · estado {cai.estado}</div>
        </div>
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase text-text-muted"><tr>
            <th className="p-2 text-left">Registrado</th><th className="p-2 text-left">Acción</th><th className="p-2 text-left">Por</th>
            <th className="p-2 text-left">Fecha efectiva</th><th className="p-2 text-left">Antes → Después</th></tr></thead>
          <tbody>
            {(cai.historial || []).length === 0 && <tr><td colSpan={5} className="p-3 text-text-muted">Sin movimientos registrados (autorización anterior a este registro).</td></tr>}
            {(cai.historial || []).map((h, i) => (
              <tr key={i} className="border-t border-border/40 align-top">
                <td className="p-2 whitespace-nowrap">{fmtDate(h.fecha)}</td>
                <td className="p-2">{ACCION[h.accion] || h.accion}{h.motivo ? ` · ${h.motivo}` : ""}</td>
                <td className="p-2">{h.por}</td>
                <td className="p-2 whitespace-nowrap">{fmtDate(h.efectiva)}</td>
                <td className="p-2 text-text-muted">{JSON.stringify(h.antes || {})} → {JSON.stringify(h.despues || {})}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-3 text-right border-t border-border"><button onClick={vm.cerrar} className="text-xs px-3 py-1.5 border border-border rounded">Cerrar</button></div>
      </div>
    </div>
  );
}

/* Autorización vigente de una (sucursal × documento), con alertas y programadas */
function CaiCard({ tipo, cai, programadas, onNew }) {
  const label = TIPO_LABEL[tipo] || tipo;
  const prog = programadas[0];
  const progLine = prog && (
    <div className="text-[11px] text-agro-yellow flex items-center gap-1" data-testid={`cai-prog-${tipo}`}>
      <CalendarPlusIcon size={12} /> Programada: entra en vigor el {fmtDate(prog.fecha_inicio)} a las 00:00
    </div>
  );
  if (!cai) {
    return (
      <div className="p-4 flex flex-col gap-2" data-testid={`cai-card-${tipo}-empty`}>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border self-start ${TIPO_COLOR[tipo]}`}>{label}</span>
        <div className="text-xs text-text-muted mt-1">Sin autorización vigente. Registra una antes de emitir.</div>
        {progLine}
        <button onClick={onNew} className="mt-1 text-xs text-agro-green hover:underline flex items-center gap-1 self-start"
                data-testid={`btn-new-cai-${tipo}`}>
          <PlusIcon size={12} weight="bold" /> Registrar autorización
        </button>
      </div>
    );
  }
  const alertPocos = cai.documentos_restantes <= Math.max(30, Math.round((cai.correlativo_final - cai.correlativo_inicial + 1) * 0.1));
  const dias = (() => {
    if (!cai.fecha_limite) return 999;
    try { return Math.ceil((parseFecha(cai.fecha_limite) - new Date(new Date().toDateString())) / 86400000); } catch { return 999; }
  })();
  const alertVence = dias <= 30;
  return (
    <div className="p-4 flex flex-col gap-1.5" data-testid={`cai-card-${tipo}`}>
      <div className="flex items-center gap-2">
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${TIPO_COLOR[tipo]}`}>{label}</span>
        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ml-auto ${ESTADO_CLS[cai.estado] || ESTADO_CLS.vigente}`}>{cai.estado || "vigente"}</span>
      </div>
      <div className="text-xs font-mono text-text-secondary break-all">{cai.numero_cai}</div>
      <div className="text-[11px] text-text-muted flex items-center gap-1.5"><HashIcon size={11} />
        Rango <span className="font-mono text-text-secondary">{cai.rango_inicial.split("-").pop()}</span> —
        <span className="font-mono text-text-secondary">{cai.rango_final.split("-").pop()}</span></div>
      <div className="text-[11px] text-text-muted flex items-center gap-1.5">
        <CalendarBlankIcon size={11} className={alertVence ? "text-agro-yellow" : ""} />
        Vence <span className={`font-mono ${alertVence ? "text-agro-yellow" : "text-text-secondary"}`}>{cai.fecha_limite ? fmtDate(cai.fecha_limite) : "SIN FECHA LÍMITE"}</span></div>
      <div className="text-[11px] text-text-muted flex items-center gap-1.5 min-w-0"><MapPinIcon size={11} />
        <span className="truncate" title={cai.direccion_fiscal}>{cai.direccion_fiscal}</span></div>
      <div className="text-[11px] text-text-muted">
        Próximo <span className="font-mono text-text-secondary">{String(cai.correlativo_actual).padStart(8, "0")}</span> ·
        emitidos <span className="text-agro-yellow font-mono">{cai.documentos_emitidos}</span> ·
        restan <span className={`font-mono ${alertPocos ? "text-agro-yellow" : "text-agro-green"}`}>{cai.documentos_restantes}</span>
      </div>
      {progLine}
      {(alertPocos || alertVence) && !prog && (
        <div className="mt-1 flex items-start gap-1.5 text-[11px] text-agro-yellow bg-agro-yellow/10 border border-agro-yellow/30 rounded p-2">
          <WarningIcon size={13} weight="fill" className="flex-shrink-0 mt-0.5" />
          <span>
            {alertVence && dias < 0 && "La autorización está vencida. "}
            {alertVence && dias >= 0 && `Vence en ${dias} días. `}
            {alertPocos && `Solo ${cai.documentos_restantes} correlativos disponibles. `}
            Programa la siguiente autorización.
          </span>
        </div>
      )}
      <button onClick={onNew} className="mt-1 text-xs text-agro-green hover:underline flex items-center gap-1 self-start"
              data-testid={`btn-renew-cai-${tipo}`}>
        <PlusIcon size={12} weight="bold" /> Renovar / programar
      </button>
    </div>
  );
}
